import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  collection, onSnapshot, query, where, addDoc, serverTimestamp,
} from 'firebase/firestore';

const HOURS = Array.from({ length: 22 }, (_, i) => {
  const h = 8 + Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
}); // 8:00 AM – 6:30 PM

function formatTime(t) {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function todayLocalStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function getNext14Days() {
  const days = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    days.push({ iso, label, dow: d.getDay() });
  }
  return days;
}

/** Build Google Calendar URL */
function googleCalUrl(serviceName, date, time, duration) {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, m] = time.split(':').map(Number);
  const start = new Date(y, mo - 1, d, h, m);
  const end   = new Date(start.getTime() + duration * 60000);
  const fmt = (dt) =>
    `${dt.getFullYear()}${String(dt.getMonth()+1).padStart(2,'0')}${String(dt.getDate()).padStart(2,'0')}T${String(dt.getHours()).padStart(2,'0')}${String(dt.getMinutes()).padStart(2,'0')}00`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text:   `✂️ ${serviceName} @ Eric G Cuts`,
    dates:  `${fmt(start)}/${fmt(end)}`,
    details:'Eric G Cuts · 10989 Peoria St, Sun Valley, CA 91352 · (818) 641-2094',
    location:'10989 Peoria St, Sun Valley, CA 91352',
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}

/** Build .ics file download for Apple Calendar */
function buildICS(serviceName, date, time, duration) {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, m] = time.split(':').map(Number);
  const start = new Date(y, mo - 1, d, h, m);
  const end   = new Date(start.getTime() + duration * 60000);
  const fmt = (dt) =>
    `${dt.getFullYear()}${String(dt.getMonth()+1).padStart(2,'0')}${String(dt.getDate()).padStart(2,'0')}T${String(dt.getHours()).padStart(2,'0')}${String(dt.getMinutes()).padStart(2,'0')}00`;
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//EricGCuts//EN',
    'BEGIN:VEVENT',
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:✂️ ${serviceName} @ Eric G Cuts`,
    'LOCATION:10989 Peoria St, Sun Valley, CA 91352',
    'DESCRIPTION:Eric G Cuts · (818) 641-2094',
    `UID:${Date.now()}@ericgcuts.com`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

function downloadICS(serviceName, date, time, duration) {
  const ics  = buildICS(serviceName, date, time, duration);
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = 'appointment.ics';
  a.click();
  URL.revokeObjectURL(url);
}

export default function Book() {
  const navigate  = useNavigate();
  const name      = localStorage.getItem('ericgcuts_name') || '';
  const phone     = auth.currentUser?.phoneNumber || '';

  const [services,    setServices]    = useState([]);
  const [bookedMap,   setBookedMap]   = useState({}); // date → [time, ...]
  const [service,     setService]     = useState(null);
  const [date,        setDate]        = useState(todayLocalStr());
  const [time,        setTime]        = useState('');
  const [step,        setStep]        = useState('service'); // service | datetime | confirm
  const [saving,      setSaving]      = useState(false);
  const [done,        setDone]        = useState(false);
  const [joinWaitlist, setJoinWaitlist] = useState(false); // true when tapping a taken slot
  const [waitlistTime, setWaitlistTime] = useState('');
  const [waitlistSaving, setWaitlistSaving] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);

  const days = getNext14Days();

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'services'), snap => {
      setServices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  useEffect(() => {
    const today = todayLocalStr();
    const q = query(
      collection(db, 'appointments'),
      where('date', '>=', today),
      where('status', 'in', ['scheduled', 'pending'])
    );
    const unsub = onSnapshot(q, snap => {
      const map = {};
      snap.docs.forEach(d => {
        const a = d.data();
        // Handle both camelCase and snake_case field names
        const aDate = a.date || '';
        const aTime = a.time || '';
        if (!map[aDate]) map[aDate] = [];
        map[aDate].push(aTime);
      });
      setBookedMap(map);
    });
    return unsub;
  }, []);

  const takenSlots     = bookedMap[date] || [];
  const availableSlots = HOURS.filter(h => !takenSlots.includes(h));
  const fullSlots      = HOURS.filter(h => takenSlots.includes(h));

  async function book() {
    if (!service || !date || !time) return;
    setSaving(true);
    try {
      const cleanPhone = (phone || '').replace(/\D/g, '');
      await addDoc(collection(db, 'appointments'), {
        clientName:  name,
        clientPhone: cleanPhone,
        serviceName: service.name,
        duration:    service.duration,
        price:       service.price,
        date,
        time,
        status:      'pending',
        createdAt:   serverTimestamp(),
      });
      setDone(true);
    } catch (e) {
      console.error(e);
      alert('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function joinWaitlistForSlot() {
    setWaitlistSaving(true);
    try {
      const cleanPhone = (phone || '').replace(/\D/g, '');
      await addDoc(collection(db, 'waitlist'), {
        clientName:  name,
        clientPhone: cleanPhone,
        date,
        time:        waitlistTime,
        createdAt:   serverTimestamp(),
      });
      setWaitlistDone(true);
      setJoinWaitlist(false);
    } catch (e) {
      console.error(e);
      alert('Could not join waitlist. Try again.');
    } finally {
      setWaitlistSaving(false);
    }
  }

  // ── Booking confirmed view ────────────────────────────────────────────────
  if (done) {
    const selectedDay = days.find(d => d.iso === date);
    return (
      <div style={{ padding: 24, paddingTop: 80, textAlign: 'center' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 8 }}>Request Sent!</div>
        <div style={{ fontSize: 15, color: 'var(--subtext)', lineHeight: 1.6, marginBottom: 32 }}>
          Eric will confirm your appointment shortly. You'll see it on your home screen once confirmed.
        </div>

        {/* Calendar integration */}
        <div style={{
          background: 'var(--card)', borderRadius: 18, border: '1px solid var(--border)',
          padding: 20, marginBottom: 24, textAlign: 'left',
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            📅 Add to your calendar
          </div>
          <div style={{ fontSize: 13, color: 'var(--subtext)', marginBottom: 16 }}>
            {service?.name} · {selectedDay?.label} at {formatTime(time)}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <a
              href={googleCalUrl(service?.name || '', date, time, service?.duration || 60)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1, background: 'var(--card)', border: '2px solid var(--border)',
                borderRadius: 12, padding: '12px 8px', fontSize: 14, fontWeight: 700,
                color: 'var(--text)', textAlign: 'center', textDecoration: 'none',
              }}
            >
              🗓️ Google Calendar
            </a>
            <button
              onClick={() => downloadICS(service?.name || '', date, time, service?.duration || 60)}
              style={{
                flex: 1, background: 'var(--card)', border: '2px solid var(--border)',
                borderRadius: 12, padding: '12px 8px', fontSize: 14, fontWeight: 700,
                color: 'var(--text)', cursor: 'pointer',
              }}
            >
              🍎 Apple Calendar
            </button>
          </div>
        </div>

        <button
          onClick={() => navigate('/')}
          style={{
            background: 'var(--accent)', borderRadius: 16,
            padding: '16px 40px', fontSize: 17, fontWeight: 900, color: '#000',
            border: 'none', cursor: 'pointer', width: '100%',
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  // ── Waitlist confirmation overlay ────────────────────────────────────────
  if (joinWaitlist) return (
    <div style={{ padding: 24, paddingTop: 80, textAlign: 'center' }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>📋</div>
      <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>Join Waitlist?</div>
      <div style={{ fontSize: 15, color: 'var(--subtext)', lineHeight: 1.6, marginBottom: 32 }}>
        {formatTime(waitlistTime)} on {days.find(d => d.iso === date)?.label} is taken.
        Join the waitlist and Eric will notify you if it opens up.
      </div>
      {waitlistDone ? (
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--accent)', marginBottom: 24 }}>
          ✓ You're on the waitlist!
        </div>
      ) : (
        <button
          onClick={joinWaitlistForSlot}
          disabled={waitlistSaving}
          style={{
            width: '100%', background: 'var(--accent)', borderRadius: 14,
            padding: 18, fontSize: 17, fontWeight: 900, color: '#000',
            border: 'none', cursor: 'pointer', opacity: waitlistSaving ? 0.5 : 1, marginBottom: 12,
          }}
        >
          {waitlistSaving ? 'Joining…' : '📋 Join Waitlist'}
        </button>
      )}
      <button
        onClick={() => { setJoinWaitlist(false); setWaitlistDone(false); }}
        style={{
          background: 'none', color: 'var(--subtext)', fontSize: 15,
          border: 'none', cursor: 'pointer', padding: '12px 0', width: '100%',
        }}
      >
        ← Back to time selection
      </button>
    </div>
  );

  // ── Step: Service ─────────────────────────────────────────────────────────
  if (step === 'service') return (
    <div style={{ padding: 24, paddingTop: 48 }}>
      <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 4 }}>Pick a Service</div>
      <div style={{ fontSize: 14, color: 'var(--subtext)', marginBottom: 24 }}>What are you coming in for?</div>

      {services.length === 0 && (
        <div style={{ color: 'var(--subtext)', textAlign: 'center', paddingTop: 40 }}>
          Loading services…
        </div>
      )}

      {services.map(s => (
        <button
          key={s.id}
          onClick={() => { setService(s); setStep('datetime'); }}
          style={{
            width: '100%', background: 'var(--card)', borderRadius: 16,
            border: `2px solid ${service?.id === s.id ? 'var(--accent)' : 'var(--border)'}`,
            padding: '18px 20px', marginBottom: 10, cursor: 'pointer',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{s.name}</div>
            <div style={{ fontSize: 13, color: 'var(--subtext)', marginTop: 3 }}>{s.duration} min</div>
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent)' }}>${Number(s.price).toFixed(0)}</div>
        </button>
      ))}
    </div>
  );

  // ── Step: Date & Time ─────────────────────────────────────────────────────
  if (step === 'datetime') return (
    <div style={{ padding: 24, paddingTop: 48 }}>
      <button
        onClick={() => setStep('service')}
        style={{ background: 'none', color: 'var(--subtext)', fontSize: 15, marginBottom: 16, padding: 0, border: 'none', cursor: 'pointer' }}
      >
        ← Back
      </button>
      <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 4 }}>Pick a Time</div>
      <div style={{ fontSize: 14, color: 'var(--subtext)', marginBottom: 20 }}>{service?.name}</div>

      {/* Day selector */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 12, marginBottom: 20 }}>
        {days.map(d => (
          <button
            key={d.iso}
            onClick={() => { setDate(d.iso); setTime(''); }}
            style={{
              flexShrink: 0, padding: '10px 14px', borderRadius: 12,
              border: `2px solid ${date === d.iso ? 'var(--accent)' : 'var(--border)'}`,
              background: date === d.iso ? 'var(--accent-dim)' : 'var(--card)',
              color: date === d.iso ? 'var(--accent)' : 'var(--subtext)',
              fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', cursor: 'pointer',
            }}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Available time slots */}
      {availableSlots.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--subtext)', marginBottom: 12, textTransform: 'uppercase' }}>
            Available Times
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
            {availableSlots.map(h => (
              <button
                key={h}
                onClick={() => setTime(h)}
                style={{
                  padding: '10px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                  border: `2px solid ${time === h ? 'var(--accent)' : 'var(--border)'}`,
                  background: time === h ? 'var(--accent-dim)' : 'var(--card)',
                  color: time === h ? 'var(--accent)' : 'var(--subtext)',
                  cursor: 'pointer',
                }}
              >
                {formatTime(h)}
              </button>
            ))}
          </div>
        </>
      )}

      {availableSlots.length === 0 && fullSlots.length === 0 && (
        <div style={{ color: 'var(--subtext)', fontSize: 14, textAlign: 'center', paddingTop: 20, marginBottom: 24 }}>
          No times available on this day
        </div>
      )}

      {/* Taken slots — show as waitlist option */}
      {fullSlots.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--subtext)', marginBottom: 12, textTransform: 'uppercase' }}>
            Fully Booked — Join Waitlist
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
            {fullSlots.map(h => (
              <button
                key={h}
                onClick={() => { setWaitlistTime(h); setJoinWaitlist(true); }}
                style={{
                  padding: '10px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                  border: '2px solid var(--border)',
                  background: 'var(--card)',
                  color: 'var(--subtext)',
                  cursor: 'pointer',
                  opacity: 0.6,
                  position: 'relative',
                }}
              >
                {formatTime(h)} 📋
              </button>
            ))}
          </div>
        </>
      )}

      <button
        onClick={() => time && setStep('confirm')}
        style={{
          width: '100%', background: 'var(--accent)', borderRadius: 14,
          padding: 18, fontSize: 17, fontWeight: 900, color: '#000',
          opacity: time ? 1 : 0.35, border: 'none', cursor: time ? 'pointer' : 'not-allowed',
        }}
        disabled={!time}
      >
        Next →
      </button>
    </div>
  );

  // ── Step: Confirm ─────────────────────────────────────────────────────────
  const selectedDay = days.find(d => d.iso === date);
  return (
    <div style={{ padding: 24, paddingTop: 48 }}>
      <button
        onClick={() => setStep('datetime')}
        style={{ background: 'none', color: 'var(--subtext)', fontSize: 15, marginBottom: 16, padding: 0, border: 'none', cursor: 'pointer' }}
      >
        ← Back
      </button>
      <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 24 }}>Confirm Booking</div>

      <div style={{
        background: 'var(--card)', borderRadius: 18, border: '1px solid var(--border)',
        padding: 22, marginBottom: 24,
      }}>
        {[
          ['Service',  service?.name],
          ['Date',     selectedDay?.label],
          ['Time',     formatTime(time)],
          ['Duration', `${service?.duration} min`],
          ['Price',    `$${Number(service?.price).toFixed(0)}`],
          ['Name',     name],
        ].map(([label, value], i, arr) => (
          <div key={label} style={{
            display: 'flex', justifyContent: 'space-between',
            paddingTop: 14, paddingBottom: 14,
            borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
          }}>
            <span style={{ color: 'var(--subtext)', fontSize: 15 }}>{label}</span>
            <span style={{
              fontSize: 15, fontWeight: 700,
              color: label === 'Price' ? 'var(--accent)' : 'var(--text)',
            }}>{value}</span>
          </div>
        ))}
      </div>

      <div style={{
        background: 'var(--accent-dim)', borderRadius: 12, border: '1px solid var(--accent)',
        padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--accent)', lineHeight: 1.5,
      }}>
        ⚡ Eric will confirm your appointment shortly after receiving your request.
      </div>

      <button
        onClick={book}
        disabled={saving}
        style={{
          width: '100%', background: 'var(--accent)', borderRadius: 14,
          padding: 20, fontSize: 17, fontWeight: 900, color: '#000',
          opacity: saving ? 0.5 : 1, border: 'none', cursor: 'pointer',
        }}
      >
        {saving ? 'Sending…' : '✂️  Request Appointment'}
      </button>
    </div>
  );
}
